import { Injectable } from '@angular/core';
import { ISceneTemplate, ITemplateBlock } from './scene-card-template.service';
import { IPlayerProps, ISceneBlock } from '../interfaces';
import { Lib } from '../../libs/Lib';
import * as _ from 'lodash';

/**
 * Service to convert card templates from card-constants.json into actual SignStudio scenes
 *
 * This service maps the abstract template blocks (with percentage-based positioning)
 * into concrete scene JSON with proper components, colors, effects, and layered backgrounds.
 *
 * Block Type Mapping:
 * - header/text/textbox -> Label component (3241) with layered backgrounds
 * - icon -> Icon component (8001) with background layers
 * - mimeType/image -> Label with background (for first release, image support later)
 * - chart -> Label with background (chart support later)
 *
 * Layering Strategy:
 * 1. Background labels (no text, just colored backgrounds positioned behind elements)
 * 2. Background effects (subtle color fills for depth)
 * 3. Combined layers (light background + dark label overlay for depth)
 *
 * All scenes use template dimensions (supports multiple resolutions)
 */
@Injectable({
    providedIn: 'root'
})
export class CardSceneCreatorService {

    // Block player type IDs
    private readonly PLAYER_TYPES = {
        SCENE: '3510',
        LABEL: '3241',
        ICON: '8001',
        IMAGE: '3130'
    };

    // Predefined color palette (Material Design inspired) - stored as DECIMAL
    private readonly COLOR_PALETTE = {
        // === BACKGROUNDS ===
        BACKGROUND_LIGHT: '16777215',      // #FFFFFF - White
        BACKGROUND_DARK: '3093272',        // #2F3437 - Dark gray
        BACKGROUND_CARD: '15790320',       // #F0F0F0 - Very light gray
        BACKGROUND_PRIMARY: '2196243',     // #2196F3 - Blue 500
        BACKGROUND_ACCENT: '16007990',     // #F44336 - Red 500

        // === TEXT COLORS ===
        TEXT_PRIMARY: '0',                 // #000000 - Black
        TEXT_SECONDARY: '6182178',         // #5E5E62 - Gray
        TEXT_ON_PRIMARY: '16777215',       // #FFFFFF - White
        TEXT_HEADING: '2301728',           // #232020 - Almost black
        TEXT_BODY: '6710886',              // #666666 - Medium gray

        // === ICON COLORS ===
        ICON_PRIMARY: '2196243',           // #2196F3 - Blue 500
        ICON_SECONDARY: '9159498',         // #8BC34A - Light Green 500
        ICON_ACCENT: '16744448',           // #FF9800 - Orange 500
        ICON_NEUTRAL: '10395294',          // #9E9E9E - Gray 500

        // === FILL COLORS (for backgrounds and colored sprites) ===
        FILL_PRIMARY: '2196243',           // #2196F3 - Blue 500
        FILL_SECONDARY: '4149685',         // #3F51B5 - Indigo 500
        FILL_ACCENT: '15277667',           // #E91E63 - Pink 500
        FILL_SUCCESS: '5025616',           // #4CAF50 - Green 500
        FILL_WARNING: '16761095',          // #FFC107 - Amber 500
        FILL_INFO: '240884',               // #03A9F4 - Light Blue 500
        FILL_PURPLE: '9055202',            // #8A2BE2 - Blue Violet
        FILL_ORANGE: '16744448',           // #FF9800 - Orange 500
        FILL_TEAL: '56832',                // #00DCD0 - Cyan/Teal

        // === LAYER BACKGROUNDS (for depth) ===
        LAYER_LIGHT_1: '16119285',         // #F5F5F5 - Very light gray (background layer)
        LAYER_LIGHT_2: '15658734',         // #EEEEEE - Light gray (mid layer)
        LAYER_DARK_1: '5592405',           // #555555 - Dark gray
        LAYER_DARK_2: '3355443',           // #333333 - Darker gray
        LAYER_BLUE_LIGHT: '14605278',      // #DEF3FE - Very light blue
        LAYER_BLUE_MID: '12961221',        // #C5E1F5 - Light blue
        LAYER_PURPLE_LIGHT: '15066597',    // #E5D5F5 - Very light purple
        LAYER_ORANGE_LIGHT: '16760319',    // #FFEBDF - Very light orange

        // === BORDERS & SHADOWS ===
        BORDER_DEFAULT: '0',               // #000000 - Black
        BORDER_LIGHT: '13421772',          // #CCCCCC - Light gray
        BORDER_ACCENT: '2196243',          // #2196F3 - Blue 500
        SHADOW_DEFAULT: '8421504',         // #808080 - Gray
        SHADOW_DARK: '5592405',            // #555555 - Dark shadow
    };

    // Font families
    private readonly FONTS = {
        HEADING: 'Arial',
        BODY: 'Arial',
        MONO: 'Courier New'
    };

    constructor() {}

    /**
     * Main entry point: Convert a template to a complete scene with layered backgrounds
     */
    createSceneFromTemplate(template: ISceneTemplate, sceneName?: string): { Player: ISceneBlock } {
        console.log(`[CardSceneCreatorService] Creating scene from template: ${template.name}`, {
            resolution: template.resolution,
            dimensions: `${template.width}x${template.height}`,
            blockCount: template.blocks.length
        });

        const sceneId = _.uniqueId();
        const timestamp = new Date().getTime().toString();

        // Create the root scene structure with template dimensions
        const scene: { Player: ISceneBlock } = {
            Player: {
                $: {
                    player: this.PLAYER_TYPES.SCENE,
                    label: sceneName || template.name || 'New Scene',
                    folder: '',
                    autoScaling: '1',
                    mimeType: 'mainPlayer',
                    id: sceneId,
                    lastModified: timestamp,
                    changelist_id: '0',
                    accessKey: '0',
                    private_child: false
                },
                Metadata: [{
                    Kiosk: [{
                        $: {
                            mode: '0',
                            key: 'password'
                        }
                    }]
                }],
                Data: [{
                    Layout: [{
                        $: {
                            height: template.height.toString(),
                            rotation: '0',
                            width: template.width.toString(),
                            x: '0',
                            y: '0'
                        }
                    }],
                    Scene: [{
                        $: {
                            defaultDuration: '10',
                            scrolling: '0'
                        },
                        Layout: [],
                        Players: [{
                            Player: this.createPlayerBlocksWithLayers(template)
                        }]
                    }]
                }]
            }
        };

        console.log(`[CardSceneCreatorService] Created scene with ${template.blocks.length} blocks and layered backgrounds`);
        return scene;
    }

    /**
     * Convert template blocks to Player components WITH layered backgrounds
     * This creates both background layers AND foreground content for depth
     */
    private createPlayerBlocksWithLayers(template: ISceneTemplate): ISceneBlock[] {
        const players: ISceneBlock[] = [];

        // PHASE 1: Create background layers (rendered first, behind everything)
        template.blocks.forEach((block, index) => {
            const backgroundLayers = this.createBackgroundLayers(block, index, template);
            players.push(...backgroundLayers);
        });

        // PHASE 2: Create foreground content (rendered on top)
        template.blocks.forEach((block, index) => {
            const player = this.createPlayerFromBlock(block, index, template);
            if (player) {
                players.push(player);
            }
        });

        return players;
    }

    /**
     * Create layered backgrounds for a block to add depth and style
     * Returns 1-3 background label blocks positioned behind the main content
     *
     * LAYERING STRATEGY (back to front):
     * Layer 1 (furthest back): Largest, lightest color - creates depth base
     * Layer 2 (middle): Medium size, medium color - transition layer
     * Layer 3 (closest): Smallest, darkest/accent color - immediate background
     * Content (front): Original size - the actual content
     *
     * This creates a VISIBLE stacked effect where each layer is progressively smaller
     */
    private createBackgroundLayers(block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock[] {
        const layers: ISceneBlock[] = [];
        const layout = this.calculateLayout(block, template.width, template.height);

        // Determine if this block should have background layers
        const shouldHaveLayer = block.type === 'header' || block.type === 'text' || block.type === 'textbox' ||
                                block.type === 'icon' || block.type === 'chart' || block.type === 'title' ||
                                block.type === 'bullets' || block.type === 'button' || block.type === 'field';

        if (!shouldHaveLayer) {
            return layers; // No background layers for mimeType/image/video blocks
        }

        // STRATEGY 1: Multi-layer card system for headers, textboxes, titles, and buttons (3 layers)
        if (block.type === 'header' || block.type === 'textbox' || block.type === 'title' || block.type === 'button') {
            // Layer 1: Base layer (largest, furthest back)
            const baseLayer = this.createCardLayer(layout, block, index, 1);
            layers.push(baseLayer);

            // Layer 2: Mid layer (medium size)
            const midLayer = this.createCardLayer(layout, block, index, 2);
            layers.push(midLayer);

            // Layer 3: Top layer (smallest, closest to content)
            const topLayer = this.createCardLayer(layout, block, index, 3);
            layers.push(topLayer);
        }

        // STRATEGY 2: Dual-layer system for text, bullets, and fields (2 layers)
        if (block.type === 'text' || block.type === 'bullets' || block.type === 'field') {
            // Layer 1: Wide background panel
            const bgPanel = this.createTextBackgroundPanel(layout, block, index, 1);
            layers.push(bgPanel);

            // Layer 2: Accent stripe (for text and bullets, not fields)
            if ((block.type === 'text' || block.type === 'bullets') && index % 3 === 0) {
                const accentStripe = this.createAccentStripe(layout, block, index);
                layers.push(accentStripe);
            }
        }

        // STRATEGY 3: Triple-layer icon background (3 layers)
        if (block.type === 'icon' && !block.rounded) {
            // Layer 1: Outer glow/halo (largest)
            const outerGlow = this.createIconBackground(layout, block, index, 1);
            layers.push(outerGlow);

            // Layer 2: Mid circle (medium)
            const midCircle = this.createIconBackground(layout, block, index, 2);
            layers.push(midCircle);

            // Layer 3: Inner circle (smallest, closest)
            const innerCircle = this.createIconBackground(layout, block, index, 3);
            layers.push(innerCircle);
        }

        // STRATEGY 4: Dual-layer chart container (2 layers)
        if (block.type === 'chart') {
            // Layer 1: Outer frame
            const outerFrame = this.createChartContainer(layout, block, index, 1);
            layers.push(outerFrame);

            // Layer 2: Inner container
            const innerContainer = this.createChartContainer(layout, block, index, 2);
            layers.push(innerContainer);
        }

        return layers;
    }

    /**
     * Create a card-style background layer with progressive sizing
     * @param layerNum 1=largest (back), 2=medium (mid), 3=smallest (front)
     */
    private createCardLayer(layout: any, block: ITemplateBlock, index: number, layerNum: number): ISceneBlock {
        const blockId = _.uniqueId(`__bg_card_L${layerNum}_`);

        // Progressive padding: Layer 1 (largest), Layer 2 (medium), Layer 3 (smallest)
        const paddingMap = {
            1: 40,  // Furthest back - largest padding
            2: 25,  // Middle layer
            3: 12   // Closest - smallest padding (just visible behind content)
        };
        const padding = paddingMap[layerNum] || 20;

        const cardLayout = {
            x: layout.x - padding,
            y: layout.y - padding,
            width: layout.width + (padding * 2),
            height: layout.height + (padding * 2)
        };

        // Progressive colors: lighter in back, darker in front
        const colorMap = {
            1: block.special ? this.COLOR_PALETTE.LAYER_BLUE_LIGHT : this.COLOR_PALETTE.LAYER_LIGHT_1,  // Lightest
            2: block.special ? this.COLOR_PALETTE.LAYER_BLUE_MID : this.COLOR_PALETTE.LAYER_LIGHT_2,    // Medium
            3: block.special ? this.COLOR_PALETTE.FILL_PRIMARY : this.COLOR_PALETTE.BACKGROUND_CARD      // Darkest/accent
        };

        // Progressive corner radius: more rounded in back, less in front
        const radiusMap = {
            1: '16',  // Most rounded
            2: '12',  // Medium rounded
            3: '8'    // Least rounded
        };

        // Progressive shadow: strongest in back, weakest in front
        const shadowMap = {
            1: { alpha: '0.25', blur: '16', distance: '12' },  // Strongest shadow
            2: { alpha: '0.18', blur: '12', distance: '8' },   // Medium shadow
            3: { alpha: '0.10', blur: '8', distance: '4' }     // Lightest shadow
        };

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Background Card L${layerNum} ${index}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: cardLayout.x.toString(),
                        y: cardLayout.y.toString(),
                        width: cardLayout.width.toString(),
                        height: cardLayout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [''],
                    Font: [{
                        $: {
                            fontSize: '14',
                            fontColor: '0',
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Background: [{
                    $: {
                        color: colorMap[layerNum],
                        alpha: '1',
                        style: ''
                    }
                }],
                Border: [{
                    $: {
                        cornerRadius: radiusMap[layerNum],
                        borderThickness: '0',
                        borderColor: '0'
                    }
                }],
                Shadow: [{
                    $: {
                        alpha: shadowMap[layerNum].alpha,
                        angle: '45',
                        blur: shadowMap[layerNum].blur,
                        color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                        distance: shadowMap[layerNum].distance,
                        resolution: '1',
                        blurX: shadowMap[layerNum].blur,
                        blurY: shadowMap[layerNum].blur,
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create a wide background panel behind text blocks
     */
    private createTextBackgroundPanel(layout: any, block: ITemplateBlock, index: number, layerNum: number): ISceneBlock {
        const blockId = _.uniqueId(`__bg_text_panel_`);

        // Wider padding for horizontal emphasis
        const padding = 18;
        const panelLayout = {
            x: layout.x - padding,
            y: layout.y - 8,
            width: layout.width + (padding * 2),
            height: layout.height + 16
        };

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Text Panel ${index}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: panelLayout.x.toString(),
                        y: panelLayout.y.toString(),
                        width: panelLayout.width.toString(),
                        height: panelLayout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [''],
                    Font: [{
                        $: {
                            fontSize: '14',
                            fontColor: '0',
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Background: [{
                    $: {
                        color: this.COLOR_PALETTE.LAYER_LIGHT_1,
                        alpha: '0.8',
                        style: ''
                    }
                }],
                Border: [{
                    $: {
                        cornerRadius: '6',
                        borderThickness: '0',
                        borderColor: '0'
                    }
                }],
                Shadow: [{
                    $: {
                        alpha: '0.08',
                        angle: '45',
                        blur: '6',
                        color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                        distance: '3',
                        resolution: '1',
                        blurX: '6',
                        blurY: '6',
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create an accent stripe/bar behind text for emphasis
     */
    private createAccentStripe(layout: any, block: ITemplateBlock, index: number): ISceneBlock {
        const blockId = _.uniqueId('__bg_stripe_');

        // Thin vertical stripe on the left edge
        const stripeLayout = {
            x: layout.x - 8,
            y: layout.y,
            width: 4,
            height: layout.height
        };

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Accent Stripe ${index}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: stripeLayout.x.toString(),
                        y: stripeLayout.y.toString(),
                        width: stripeLayout.width.toString(),
                        height: stripeLayout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [''],
                    Font: [{
                        $: {
                            fontSize: '14',
                            fontColor: '0',
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Background: [{
                    $: {
                        color: this.COLOR_PALETTE.FILL_PRIMARY,
                        alpha: '1',
                        style: ''
                    }
                }],
                Border: [{
                    $: {
                        cornerRadius: '2',
                        borderThickness: '0',
                        borderColor: '0'
                    }
                }]
            }]
        };
    }

    /**
     * Create a circular/square background behind icons with progressive sizing
     * @param layerNum 1=largest (back), 2=medium (mid), 3=smallest (front)
     */
    private createIconBackground(layout: any, block: ITemplateBlock, index: number, layerNum: number): ISceneBlock {
        const blockId = _.uniqueId(`__bg_icon_L${layerNum}_`);

        // Progressive padding for concentric circles
        const paddingMap = {
            1: 30,  // Outermost circle (largest)
            2: 20,  // Middle circle
            3: 10   // Inner circle (closest to icon)
        };
        const padding = paddingMap[layerNum] || 15;

        const bgLayout = {
            x: layout.x - padding,
            y: layout.y - padding,
            width: layout.width + (padding * 2),
            height: layout.height + (padding * 2)
        };

        // Progressive colors for each layer (lightest to darker)
        const colorSets = [
            // Set 1: Blues
            [this.COLOR_PALETTE.LAYER_BLUE_LIGHT, this.COLOR_PALETTE.LAYER_BLUE_MID, this.COLOR_PALETTE.FILL_PRIMARY],
            // Set 2: Purples
            [this.COLOR_PALETTE.LAYER_PURPLE_LIGHT, this.COLOR_PALETTE.FILL_PURPLE, this.COLOR_PALETTE.FILL_SECONDARY],
            // Set 3: Oranges
            [this.COLOR_PALETTE.LAYER_ORANGE_LIGHT, this.COLOR_PALETTE.FILL_ORANGE, this.COLOR_PALETTE.FILL_WARNING],
            // Set 4: Grays
            [this.COLOR_PALETTE.LAYER_LIGHT_2, this.COLOR_PALETTE.LAYER_LIGHT_1, this.COLOR_PALETTE.BACKGROUND_CARD]
        ];
        const colorSet = colorSets[index % colorSets.length];
        const color = colorSet[layerNum - 1]; // layerNum 1-3 maps to index 0-2

        // Progressive alpha: more transparent in back, more solid in front
        const alphaMap = {
            1: '0.6',  // Most transparent (glow effect)
            2: '0.8',  // Medium
            3: '1'     // Fully opaque
        };

        // Progressive shadow
        const shadowMap = {
            1: { alpha: '0.25', blur: '14', distance: '8' },
            2: { alpha: '0.18', blur: '10', distance: '6' },
            3: { alpha: '0.12', blur: '6', distance: '4' }
        };

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Icon Background L${layerNum} ${index}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: bgLayout.x.toString(),
                        y: bgLayout.y.toString(),
                        width: bgLayout.width.toString(),
                        height: bgLayout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [''],
                    Font: [{
                        $: {
                            fontSize: '14',
                            fontColor: '0',
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Background: [{
                    $: {
                        color: color,
                        alpha: alphaMap[layerNum],
                        style: ''
                    }
                }],
                Border: [{
                    $: {
                        cornerRadius: Math.round(bgLayout.width / 2).toString(), // Circular
                        borderThickness: '0',
                        borderColor: '0'
                    }
                }],
                Shadow: [{
                    $: {
                        alpha: shadowMap[layerNum].alpha,
                        angle: '45',
                        blur: shadowMap[layerNum].blur,
                        color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                        distance: shadowMap[layerNum].distance,
                        resolution: '1',
                        blurX: shadowMap[layerNum].blur,
                        blurY: shadowMap[layerNum].blur,
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create a container background for charts with dual layers
     * @param layerNum 1=outer frame (larger), 2=inner container (smaller)
     */
    private createChartContainer(layout: any, block: ITemplateBlock, index: number, layerNum: number): ISceneBlock {
        const blockId = _.uniqueId(`__bg_chart_L${layerNum}_`);

        // Progressive padding for dual-layer chart containers
        const paddingMap = {
            1: 15,  // Outer frame (larger)
            2: 6    // Inner container (smaller)
        };
        const padding = paddingMap[layerNum] || 8;

        const containerLayout = {
            x: layout.x - padding,
            y: layout.y - padding,
            width: layout.width + (padding * 2),
            height: layout.height + (padding * 2)
        };

        // Progressive colors
        const colorMap = {
            1: this.COLOR_PALETTE.LAYER_LIGHT_2,     // Outer: light gray
            2: this.COLOR_PALETTE.BACKGROUND_CARD    // Inner: very light gray
        };

        // Progressive border
        const borderMap = {
            1: { thickness: '2', color: this.COLOR_PALETTE.BORDER_LIGHT, radius: '10' },
            2: { thickness: '1', color: this.COLOR_PALETTE.BORDER_LIGHT, radius: '6' }
        };

        // Progressive shadow
        const shadowMap = {
            1: { alpha: '0.15', blur: '10', distance: '6' },
            2: { alpha: '0.08', blur: '4', distance: '2' }
        };

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Chart Container L${layerNum} ${index}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: containerLayout.x.toString(),
                        y: containerLayout.y.toString(),
                        width: containerLayout.width.toString(),
                        height: containerLayout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [''],
                    Font: [{
                        $: {
                            fontSize: '14',
                            fontColor: '0',
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Background: [{
                    $: {
                        color: colorMap[layerNum],
                        alpha: '1',
                        style: ''
                    }
                }],
                Border: [{
                    $: {
                        cornerRadius: borderMap[layerNum].radius,
                        borderThickness: borderMap[layerNum].thickness,
                        borderColor: borderMap[layerNum].color
                    }
                }],
                Shadow: [{
                    $: {
                        alpha: shadowMap[layerNum].alpha,
                        angle: '45',
                        blur: shadowMap[layerNum].blur,
                        color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                        distance: shadowMap[layerNum].distance,
                        resolution: '1',
                        blurX: shadowMap[layerNum].blur,
                        blurY: shadowMap[layerNum].blur,
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create a single Player component from a template block
     */
    private createPlayerFromBlock(block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock | null {
        // Convert percentage-based position/size to pixels using template dimensions
        const layout = this.calculateLayout(block, template.width, template.height);

        switch (block.type) {
            case 'header':
                return this.createHeaderLabel(layout, block, index, template);

            case 'title':
                return this.createTitleLabel(layout, block, index, template);

            case 'text':
                return this.createTextLabel(layout, block, index, template);

            case 'bullets':
                return this.createBulletsLabel(layout, block, index, template);

            case 'field':
                return this.createFieldLabel(layout, block, index, template);

            case 'textbox':
                return this.createTextboxComponent(layout, block, index, template);

            case 'button':
                return this.createButtonComponent(layout, block, index, template);

            case 'icon':
                return this.createIcon(layout, block, index, template);

            case 'mimeType':
            case 'image':
            case 'video':
                // For first release: colored background sprite
                // TODO: Image/Video support in future release
                return this.createImagePlaceholder(layout, block, index, template);

            case 'chart':
                // For first release: colored background with icon
                // TODO: Chart support in future release
                return this.createChartPlaceholder(layout, block, index, template);

            case 'progress':
            case 'timeline':
            case 'pyramid':
                // Special icon-based components
                return this.createSpecialIconComponent(layout, block, index, template);

            default:
                console.warn(`[CardSceneCreatorService] Unknown block type: ${block.type}`);
                return null;
        }
    }

    /**
     * Create a title label (medium-large text, styled differently from header)
     */
    private createTitleLabel(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Title ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [`Title ${index + 1}`],
                    Font: [{
                        $: {
                            fontSize: this.calculateHeaderFontSize(layout.height, template.height).toString(),
                            fontColor: block.special ? this.COLOR_PALETTE.FILL_ACCENT : this.COLOR_PALETTE.TEXT_HEADING,
                            fontFamily: this.FONTS.HEADING,
                            fontWeight: '600',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Shadow: [{
                    $: {
                        alpha: '0.15',
                        angle: '45',
                        blur: '3',
                        color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                        distance: '2',
                        resolution: '1',
                        blurX: '3',
                        blurY: '3',
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create a bullets label (text with bullet points)
     */
    private createBulletsLabel(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');
        const lines = block.lines || 4;
        const bullets = Array.from({length: lines}, (_, i) => `bullet point ${i + 1}`).join('\n');

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Bullets ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [bullets],
                    Font: [{
                        $: {
                            fontSize: this.calculateBodyFontSize(layout.height, lines).toString(),
                            fontColor: this.COLOR_PALETTE.TEXT_BODY,
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'left',
                            alignSelf: 'start',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }]
            }]
        };
    }

    /**
     * Create a field label (single line data field - used in JSON templates)
     */
    private createFieldLabel(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Field ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: ['Field Data Value'],
                    Font: [{
                        $: {
                            fontSize: Math.max(14, Math.round(layout.height * 0.6)).toString(),
                            fontColor: this.COLOR_PALETTE.TEXT_BODY,
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'left',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '0'
                        }
                    }]
                }]
            }]
        };
    }

    /**
     * Create a button component (styled label with button appearance)
     */
    private createButtonComponent(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Button ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: ['CLICK HERE'],
                    Font: [{
                        $: {
                            fontSize: Math.max(16, Math.round(layout.height * 0.4)).toString(),
                            fontColor: this.COLOR_PALETTE.TEXT_ON_PRIMARY,
                            fontFamily: this.FONTS.HEADING,
                            fontWeight: 'bold',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Background: [{
                    $: {
                        color: block.special ? this.COLOR_PALETTE.FILL_ACCENT : this.COLOR_PALETTE.FILL_PRIMARY,
                        alpha: '1',
                        style: ''
                    }
                }],
                Border: [{
                    $: {
                        cornerRadius: Math.round(layout.height / 2).toString(),
                        borderThickness: '0',
                        borderColor: '0'
                    }
                }],
                Shadow: [{
                    $: {
                        alpha: '0.3',
                        angle: '90',
                        blur: '8',
                        color: this.COLOR_PALETTE.SHADOW_DARK,
                        distance: '4',
                        resolution: '1',
                        blurX: '8',
                        blurY: '8',
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create special icon components (progress, timeline, pyramid)
     */
    private createSpecialIconComponent(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');

        // Map block type to icon
        const iconMap: {[key: string]: string} = {
            'progress': 'trending_up',
            'timeline': 'timeline',
            'pyramid': 'layers'
        };

        const iconName = iconMap[block.type] || 'star';

        return {
            $: {
                player: this.PLAYER_TYPES.ICON,
                label: `${block.type} ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Icon: [{
                    $: {
                        name: iconName,
                        color: block.special ? this.COLOR_PALETTE.ICON_ACCENT : this.COLOR_PALETTE.ICON_SECONDARY
                    }
                }],
                Background: [{
                    $: {
                        color: this.COLOR_PALETTE.LAYER_LIGHT_1,
                        alpha: '1',
                        style: ''
                    }
                }],
                Border: [{
                    $: {
                        cornerRadius: '12',
                        borderThickness: '2',
                        borderColor: block.special ? this.COLOR_PALETTE.BORDER_ACCENT : this.COLOR_PALETTE.BORDER_LIGHT
                    }
                }],
                Shadow: [{
                    $: {
                        alpha: '0.2',
                        angle: '45',
                        blur: '8',
                        color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                        distance: '4',
                        resolution: '1',
                        blurX: '8',
                        blurY: '8',
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Calculate pixel-based layout from percentage-based block with validation
     */
    private calculateLayout(block: ITemplateBlock, templateWidth: number, templateHeight: number): { x: number; y: number; width: number; height: number } {
        // Clamp percentages to valid 0-100 range
        const x = Math.max(0, Math.min(100, block.x));
        const y = Math.max(0, Math.min(100, block.y));
        const w = Math.max(0, Math.min(100, block.w));
        const h = Math.max(0, Math.min(100, block.h));

        return {
            x: Math.round((x / 100) * templateWidth),
            y: Math.round((y / 100) * templateHeight),
            width: Math.round((w / 100) * templateWidth),
            height: Math.round((h / 100) * templateHeight)
        };
    }

    /**
     * Create a header label (large text, bold)
     */
    private createHeaderLabel(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');

        // Use template name for first header
        const defaultText = index === 0 ? template.name.toUpperCase() : `SECTION ${index + 1}`;

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Header ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [defaultText],
                    Font: [{
                        $: {
                            fontSize: this.calculateHeaderFontSize(layout.height, template.height).toString(),
                            fontColor: block.special ? this.COLOR_PALETTE.FILL_PRIMARY : this.COLOR_PALETTE.TEXT_HEADING,
                            fontFamily: this.FONTS.HEADING,
                            fontWeight: 'bold',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'left',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                // Add subtle shadow for depth
                Shadow: [{
                    $: {
                        alpha: '0.2',
                        angle: '45',
                        blur: '3',
                        color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                        distance: '2',
                        resolution: '1',
                        blurX: '3',
                        blurY: '3',
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create a text label (body text)
     */
    private createTextLabel(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');
        const lines = block.lines || 3;
        const placeholder = this.generatePlaceholderText(lines);

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Text ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [placeholder],
                    Font: [{
                        $: {
                            fontSize: this.calculateBodyFontSize(layout.height, lines).toString(),
                            fontColor: this.COLOR_PALETTE.TEXT_BODY,
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'left',
                            alignSelf: 'start',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }]
            }]
        };
    }

    /**
     * Create a textbox component (for metrics/stats)
     */
    private createTextboxComponent(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Textbox ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: ['METRIC\n\n99.9%'],
                    Font: [{
                        $: {
                            fontSize: this.calculateTextFontSize(layout.height).toString(),
                            fontColor: block.special ? this.COLOR_PALETTE.FILL_PRIMARY : this.COLOR_PALETTE.TEXT_HEADING,
                            fontFamily: this.FONTS.HEADING,
                            fontWeight: block.special ? 'bold' : 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'middle',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Border: [{
                    $: {
                        cornerRadius: '8',
                        borderThickness: block.special ? '3' : '2',
                        borderColor: block.special ? this.COLOR_PALETTE.BORDER_ACCENT : this.COLOR_PALETTE.BORDER_LIGHT
                    }
                }],
                Shadow: [{
                    $: {
                        alpha: '0.1',
                        angle: '45',
                        blur: '4',
                        color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                        distance: '2',
                        resolution: '1',
                        blurX: '4',
                        blurY: '4',
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create an icon component
     */
    private createIcon(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');
        const iconName = block.iconType || this.getDefaultIconForIndex(index);
        const rounded = block.rounded || false;

        const iconBlock: ISceneBlock = {
            $: {
                player: this.PLAYER_TYPES.ICON,
                label: `Icon ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Icon: [{
                    $: {
                        name: iconName,
                        color: block.special ? this.COLOR_PALETTE.ICON_ACCENT : this.COLOR_PALETTE.ICON_PRIMARY
                    }
                }]
            }]
        };

        // Add background and rounded corners if specified
        if (rounded) {
            iconBlock.Data[0].Background = [{
                $: {
                    color: this.COLOR_PALETTE.BACKGROUND_LIGHT,
                    alpha: '1',
                    style: ''
                }
            }];

            iconBlock.Data[0].Border = [{
                $: {
                    cornerRadius: Math.round(layout.width / 2).toString(), // Circular
                    borderThickness: '0',
                    borderColor: '0'
                }
            }];

            // Add shadow for depth
            iconBlock.Data[0].Shadow = [{
                $: {
                    alpha: '0.25',
                    angle: '45',
                    blur: '8',
                    color: this.COLOR_PALETTE.SHADOW_DEFAULT,
                    distance: '4',
                    resolution: '1',
                    blurX: '8',
                    blurY: '8',
                    strength: '1'
                }
            }];
        }

        return iconBlock;
    }

    /**
     * Create image placeholder (colored background sprite)
     */
    private createImagePlaceholder(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');
        const isSpecial = block.special || false;

        // Pick varied colors for images
        const imageColors = [
            this.COLOR_PALETTE.FILL_PRIMARY,
            this.COLOR_PALETTE.FILL_INFO,
            this.COLOR_PALETTE.FILL_PURPLE,
            this.COLOR_PALETTE.FILL_TEAL,
            this.COLOR_PALETTE.FILL_SECONDARY
        ];

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Image ${index + 1}`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [''], // Empty text for colored sprite
                    Font: [{
                        $: {
                            fontSize: '22',
                            fontColor: '0',
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                // Colored background
                Background: [{
                    $: {
                        color: isSpecial ? this.COLOR_PALETTE.FILL_ACCENT : imageColors[index % imageColors.length],
                        alpha: '1',
                        style: ''
                    }
                }],
                // Add rounded corners for visual interest
                Border: [{
                    $: {
                        cornerRadius: block.rounded ? '20' : '8',
                        borderThickness: '0',
                        borderColor: '0'
                    }
                }],
                // Subtle shadow
                Shadow: [{
                    $: {
                        alpha: '0.25',
                        angle: '45',
                        blur: '12',
                        color: this.COLOR_PALETTE.SHADOW_DARK,
                        distance: '8',
                        resolution: '1',
                        blurX: '12',
                        blurY: '12',
                        strength: '1'
                    }
                }]
            }]
        };
    }

    /**
     * Create chart placeholder (colored background with icon)
     */
    private createChartPlaceholder(layout: any, block: ITemplateBlock, index: number, template: ISceneTemplate): ISceneBlock {
        const blockId = _.uniqueId('__');
        const chartType = block.chartType || 'bar';
        const iconName = block.iconType || 'insert_chart';

        return {
            $: {
                player: this.PLAYER_TYPES.LABEL,
                label: `Chart ${index + 1} (${chartType})`,
                folder: '',
                autoScaling: '1',
                id: blockId
            },
            Data: [{
                Layout: [{
                    $: {
                        rotation: '0',
                        x: layout.x.toString(),
                        y: layout.y.toString(),
                        width: layout.width.toString(),
                        height: layout.height.toString(),
                        locked: 'false'
                    }
                }],
                Label: [{
                    Text: [''],
                    Font: [{
                        $: {
                            fontSize: '22',
                            fontColor: '0',
                            fontFamily: this.FONTS.BODY,
                            fontWeight: 'normal',
                            fontStyle: 'normal',
                            textDecoration: 'none',
                            textAlign: 'center',
                            alignSelf: 'center',
                            autoScaling: '1',
                            wrap: '1'
                        }
                    }]
                }],
                Background: [{
                    $: {
                        color: this.COLOR_PALETTE.BACKGROUND_LIGHT,
                        alpha: '1',
                        style: ''
                    }
                }],
                Border: [{
                    $: {
                        cornerRadius: '8',
                        borderThickness: '0',
                        borderColor: '0'
                    }
                }]
            }]
        };
    }

    /**
     * Calculate appropriate font size for headers based on height (dynamic scaling)
     */
    private calculateHeaderFontSize(height: number, templateHeight: number): number {
        const fontSize = Math.round(height * 0.5);
        const minSize = Math.max(18, Math.round(templateHeight * 0.022)); // 2.2% of template height
        const maxSize = Math.min(200, Math.round(templateHeight * 0.111)); // 11.1% of template height
        return Math.max(minSize, Math.min(fontSize, maxSize));
    }

    /**
     * Calculate appropriate font size for body text based on height and lines
     */
    private calculateBodyFontSize(height: number, lines: number): number {
        // Divide height by lines to get per-line height, then scale
        const perLineHeight = height / lines;
        const fontSize = Math.round(perLineHeight * 0.6);
        return Math.max(14, Math.min(fontSize, 48)); // Range: 14-48px
    }

    /**
     * Calculate appropriate font size for textbox content
     */
    private calculateTextFontSize(height: number): number {
        const fontSize = Math.round(height * 0.25);
        return Math.max(18, Math.min(fontSize, 72)); // Range: 18-72px
    }

    /**
     * Generate placeholder text based on number of lines
     */
    private generatePlaceholderText(lines: number): string {
        const placeholders = [
            'your text content goes here.',
            'add your message or description.',
            'this is placeholder text for your content.',
            'edit this text to customize your scene.',
            'replace with your own text content.'
        ];

        const text: string[] = [];
        for (let i = 0; i < lines; i++) {
            text.push(placeholders[i % placeholders.length]);
        }

        return text.join('\n');
    }

    /**
     * Get default icon name based on index
     */
    private getDefaultIconForIndex(index: number): string {
        const icons = [
            'star',
            'trending_up',
            'check_circle',
            'lightbulb',
            'favorite',
            'analytics',
            'rocket_launch',
            'verified'
        ];
        return icons[index % icons.length];
    }

    /**
     * Add fade-in animation to a block
     */
    addFadeInAnimation(block: ISceneBlock): void {
        if (!block.Data[0].Animation) {
            block.Data[0].Animation = [{
                In: [{
                    $: {
                        enabled: '1',
                        delay: '0',
                        duration: '800',
                        fadeFrq: '1',
                        moveFrq: '0',
                        scaleFrq: '0',
                        rotateFrq: '0',
                        moveIntense: '1',
                        moveDir: 'fixed',
                        moveAngle: '0',
                        rotateType: 'fixed',
                        rotations: '1',
                        blurFrq: '0',
                        blurValue: '10',
                        textTypingFrq: '0'
                    }
                }],
                Out: [{
                    $: {
                        enabled: '1',
                        delay: '0',
                        duration: '600',
                        fadeFrq: '1',
                        moveFrq: '0',
                        scaleFrq: '0',
                        rotateFrq: '0',
                        moveIntense: '1',
                        moveDir: 'fixed',
                        moveAngle: '0',
                        rotateType: 'fixed',
                        rotations: '1',
                        blurFrq: '0',
                        blurValue: '10',
                        textTypingFrq: '0'
                    }
                }],
                Play: [{
                    $: {
                        enabled: '0',
                        duration: '1000',
                        frequency: '1',
                        rotateFrq: '0',
                        rotateValue: '45',
                        fadeFrq: '0',
                        scaleFrq: '0',
                        scaleValue: '1.5'
                    }
                }],
                Touch: [{
                    $: {
                        enabled: '0',
                        duration: '300',
                        scaleValue: '1.1'
                    }
                }]
            }];
        }
    }
}
